// src/pages/Login.js — Redesign v2 (fev/26)
//
// Layout 2 colunas (brand-side esquerda + form-side direita), dark theme,
// coerente com o design system do dashboard (mesmas fontes Outfit+Syne,
// mesma paleta verde/dark).
//
// IMPORTANTE (segurança):
//   - Auth flow preservado: POST /auth/login com URLSearchParams
//     (OAuth2PasswordRequestForm), token em localStorage, navigate.
//   - Input de senha é <input type="password"> REAL — compatível com
//     password managers (LastPass, 1Password, Chrome autofill). Toggle
//     do olho só troca `type` entre password ↔ text.
//   - `autoComplete` correto pros browsers reconhecerem os campos.
//   - Handling específico de 429 (rate limit adicionado no P1) — mostra
//     mensagem clara com countdown, evita usuário ver "Erro 429" cru.
//   - "Esqueceu sua senha?" leva a /recuperar-senha (out/26: código de 6
//     dígitos por e-mail). Antes abria um modal mandando escrever pro
//     suporte. Quem cai aqui com ?motivo=senha saiu porque a senha foi
//     trocada em outro lugar (services/Api.js) e vê o aviso.
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../services/Api';

const C = {
  bg: '#0A0F1A',
  bg2: '#0E1525',
  bg3: '#131C30',
  bg4: '#1A2540',
  bdr: 'rgba(255,255,255,0.07)',
  bdr2: 'rgba(255,255,255,0.12)',
  green: '#00D67C',
  greenBg: 'rgba(0,214,124,0.10)',
  greenB: 'rgba(0,214,124,0.28)',
  red: '#FF4560',
  redBg: 'rgba(255,69,96,0.12)',
  redB: 'rgba(255,69,96,0.30)',
  text: '#DDE4F0',
  text2: '#7A8799',
  text3: '#3E4D60',
};

const FONT = "'Syne','Segoe UI',sans-serif";

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [retryIn, setRetryIn] = useState(0); // countdown do 429 (segundos)
  const navigate = useNavigate();
  const location = useLocation();
  const senhaTrocada = new URLSearchParams(location.search).get('motivo') === 'senha';
  const irParaRecuperar = () => navigate('/recuperar-senha', { state: { email: email.trim() } });

  // Countdown do rate limit — se retryIn > 0, decrementa a cada segundo.
  // Quando chega a 0, libera o botão automaticamente.
  useEffect(() => {
    if (retryIn <= 0) return;
    const t = setTimeout(() => setRetryIn((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [retryIn]);

  const isDisabled = loading || retryIn > 0 || !email || !password;

  const handleLogin = async (e) => {
    e.preventDefault();
    if (isDisabled) return;
    setLoading(true);
    setStatus('');

    try {
      // FastAPI OAuth2PasswordRequestForm exige application/x-www-form-urlencoded.
      // Forçamos o header aqui porque a instância `api` tem default
      // 'application/json' que sobrescreveria o auto-detect do URLSearchParams.
      const params = new URLSearchParams();
      params.append('username', email);
      params.append('password', password);

      const res = await api.post('/auth/login', params, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });

      localStorage.setItem('token', res.data.access_token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      localStorage.setItem('company_id', res.data.user.company_id || '');
      localStorage.setItem('company_name', res.data.user.company_name || '');

      navigate('/dashboard', { replace: true });
    } catch (err) {
      // Log ajuda a distinguir 401 (creds) de 422 (parse), 429 (rate limit)
      // ou erro de rede — sem isso, usuário sempre vê a mesma mensagem.
      // eslint-disable-next-line no-console
      console.error('[login] falha:', err?.response?.status, err?.response?.data || err?.message);
      const st = err?.response?.status;
      if (st === 401) {
        setStatus('E-mail ou senha incorretos. Verifique seus dados.');
      } else if (st === 429) {
        // Rate limit adicionado no P1 (slowapi). Header Retry-After vem em
        // segundos; fallback pra 60s se não vier. Countdown desabilita o
        // botão até liberar — evita usuário martelando F5 e queimando ainda
        // mais o quota. NOTA: axios lowercase os headers em algumas versões,
        // então tentamos as duas grafias.
        const ra =
          err?.response?.headers?.['retry-after'] ||
          err?.response?.headers?.['Retry-After'];
        const seconds = Math.max(1, parseInt(ra, 10) || 60);
        setRetryIn(seconds);
        setStatus(`Muitas tentativas de login. Aguarde ${seconds}s e tente novamente.`);
      } else if (st) {
        setStatus(`Erro ${st} ao fazer login. Tente novamente.`);
      } else {
        setStatus('Não foi possível conectar. Verifique sua conexão.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Estilos escopados na classe .login-v2 pra não vazar pro resto do app.
          Global rules (body overflow) evitadas de propósito — usamos wrapper
          `position: fixed` que não afeta scroll do body. */}
      <style>{`
        .login-v2 * { box-sizing: border-box; }
        .login-v2 .pdp { animation: pdp 2s infinite; }
        @keyframes pdp {
          0%,100% { box-shadow: 0 0 0 0 rgba(0,214,124,.4); }
          50% { box-shadow: 0 0 0 6px rgba(0,214,124,0); }
        }
        @keyframes shakeIn {
          0%{transform:translateX(-4px);} 25%{transform:translateX(4px);}
          50%{transform:translateX(-2px);} 75%{transform:translateX(2px);}
          100%{transform:translateX(0);}
        }
        .login-v2 .field-input:focus-within {
          border-color: ${C.greenB};
          background: rgba(0,214,124,0.04);
        }
        .login-v2 input {
          background: transparent; border: none; outline: none;
          color: ${C.text}; font-family: ${FONT}; font-size: 13px;
          flex: 1; min-width: 0; padding: 0;
        }
        .login-v2 input::placeholder { color: ${C.text3}; font-size: 12px; }
        /* Autofill do Chrome/Edge quebra o dark theme; força de volta. */
        .login-v2 input:-webkit-autofill,
        .login-v2 input:-webkit-autofill:hover,
        .login-v2 input:-webkit-autofill:focus {
          -webkit-text-fill-color: ${C.text};
          -webkit-box-shadow: 0 0 0px 1000px ${C.bg4} inset;
          transition: background-color 5000s ease-in-out 0s;
        }
        .login-v2 .btn-submit:hover:not(:disabled) {
          background: #00f090;
          box-shadow: 0 6px 22px rgba(0,214,124,.35);
          transform: translateY(-1px);
        }
        .login-v2 .nav-link:hover { color: ${C.text}; }
        .login-v2 .register-a:hover { color: #00f090; }
        .login-v2 .pw-eye:hover { color: ${C.text2}; }
        .login-v2 .forgot-link:hover { color: ${C.green}; }
        @media (max-width: 900px) {
          /* !important é obrigatório em todos esses overrides — os
             elementos abaixo têm inline styles (display: 'grid',
             display: 'flex', padding: '40px 48px') no JSX, e specificity
             CSS diz que inline vence class-based mesmo em media query.
             Sem !important, o layout desktop persistia em <900px e o
             brand-side aparecia espremido ao lado do form (bug visível
             em screenshot do usuário). */
          .login-v2 .page {
            grid-template-columns: 1fr !important;
            overflow-y: auto !important;
            height: auto !important;
          }
          .login-v2 .brand-side { display: none !important; }
          .login-v2 .form-side {
            min-height: calc(100vh - 58px) !important;
            padding: 32px 24px !important;
          }
        }
        /* Fine-tune pra celular pequeno (iPhone SE, 375px). Reduz padding
           lateral do form pra o card do login ganhar respiração até as
           bordas da tela. Agora que o wrapper não é mais position:fixed,
           quando o teclado sobe o usuário consegue scrollar até o botão. */
        @media (max-width: 480px) {
          .login-v2 .form-side { padding: 20px 14px !important; }
        }
      `}</style>

      <div
        className="login-v2"
        style={{
          // Antes: position:fixed + inset:0 + overflow:hidden. Isso criava
          // armadilha de scroll no mobile — quando o teclado do celular
          // sobe, o form ficava cortado sem forma do usuário rolar até o
          // botão. Também travava em desktops menores (900×700) se o
          // conteúdo passasse do viewport.
          // Agora: minHeight:100vh permite scroll natural quando necessário,
          // mantendo a mesma aparência quando o conteúdo cabe.
          minHeight: '100vh',
          background: C.bg,
          color: C.text,
          fontFamily: FONT,
        }}
      >
        {/* ── TOP NAV ── */}
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
            background: 'rgba(10,15,26,0.92)', backdropFilter: 'blur(12px)',
            borderBottom: `1px solid ${C.bdr}`,
            padding: '0 32px', height: 58,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}
        >
          <div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#fff', letterSpacing: '-0.5px' }}>
              <span style={{ color: C.green }}>C</span>ETTO
            </div>
            <div style={{
              fontSize: 8, fontWeight: 700, letterSpacing: '0.2em',
              color: C.text3, textTransform: 'uppercase', marginTop: -2,
            }}>
              System
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span
              className="nav-link"
              onClick={() => navigate('/register')}
              style={{ fontSize: 12, color: C.text2, cursor: 'pointer', transition: 'color .15s' }}
            >
              Ainda não tem conta?
              <span style={{ color: C.green, fontWeight: 600, marginLeft: 4 }}>Criar conta grátis →</span>
            </span>
          </div>
        </div>

        {/* ── 2-COLUMN PAGE ── */}
        <div
          className="page"
          style={{
            paddingTop: 58, height: '100vh',
            display: 'grid', gridTemplateColumns: '1fr 1fr',
          }}
        >
          {/* ── LEFT: BRAND SIDE ── */}
          <div
            className="brand-side"
            style={{
              background: C.bg,
              display: 'flex', flexDirection: 'column', justifyContent: 'center',
              padding: '60px 56px',
              position: 'relative', overflow: 'hidden',
              borderRight: `1px solid ${C.bdr}`,
            }}
          >
            {/* background glow */}
            <div style={{
              content: '""', position: 'absolute',
              bottom: -120, left: -80, width: 500, height: 500,
              background: 'radial-gradient(circle,rgba(0,214,124,0.07) 0,transparent 65%)',
              pointerEvents: 'none',
            }} />

            <div style={{
              position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 7,
              background: C.greenBg, border: `1px solid ${C.greenB}`,
              borderRadius: 20, padding: '5px 13px',
              fontSize: 10, fontWeight: 700, letterSpacing: '.1em',
              textTransform: 'uppercase', color: C.green,
              marginBottom: 24, width: 'fit-content',
            }}>
              <span className="pdp" style={{
                width: 7, height: 7, borderRadius: '50%', background: C.green,
              }} />
              Tecnologia Oficial Meta · API em todos os planos
            </div>

            <h1 style={{
              position: 'relative', fontSize: 'clamp(22px,2.6vw,34px)',
              fontWeight: 800, letterSpacing: '-1px', lineHeight: 1.15,
              color: '#fff', marginBottom: 16, marginTop: 0,
            }}>
              Seu WhatsApp<br />em segurança.<br />Suas vendas<br />
              <span style={{ color: C.green }}>em escala.</span>
            </h1>
            <p style={{
              position: 'relative', fontSize: 14, color: C.text2, lineHeight: 1.7,
              maxWidth: 420, marginBottom: 36, marginTop: 0,
            }}>
              Gerencie chips WhatsApp com Health Score em tempo real, supervisione sua equipe
              e nunca perca um número por banimento inesperado.
            </p>

            {/* feature list */}
            <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 13, marginBottom: 36 }}>
              {[
                ['Health Score exclusivo', 'saúde de cada chip atualizada a cada 15s, antes de qualquer bloqueio.'],
                ['Supervisão em tempo real', 'leia todas as conversas dos agentes sem pedir print.'],
                ['API Oficial Meta em todos os planos', 'desde R$ 139/mês. Sem QR Code. Sem instabilidade.'],
              ].map(([bold, rest]) => (
                <div key={bold} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{
                    width: 22, height: 22, borderRadius: 6, flexShrink: 0, marginTop: 1,
                    background: C.greenBg, border: `1px solid ${C.greenB}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 11, color: C.green,
                  }}>
                    ✓
                  </div>
                  <div style={{ fontSize: 13, color: C.text2, lineHeight: 1.55 }}>
                    <strong style={{ color: C.text, fontWeight: 600 }}>{bold}</strong> — {rest}
                  </div>
                </div>
              ))}
            </div>

            {/* trust seals */}
            <div style={{ position: 'relative', display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 28 }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '4px 10px', borderRadius: 6,
                fontSize: 9.5, fontWeight: 700, letterSpacing: '.04em',
                background: C.greenBg, border: `1px solid ${C.greenB}`, color: C.green,
              }}>
                ⬡ API Oficial Meta
              </div>
              {['🔒 LGPD', '🇧🇷 100% Brasileiro'].map((s) => (
                <div key={s} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '4px 10px', borderRadius: 6,
                  fontSize: 9.5, fontWeight: 700, letterSpacing: '.04em',
                  background: C.bg3, border: `1px solid ${C.bdr}`, color: C.text3,
                }}>
                  {s}
                </div>
              ))}
            </div>
          </div>

          {/* ── RIGHT: FORM SIDE ── */}
          <div
            className="form-side"
            style={{
              background: C.bg2,
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              padding: '40px 48px', overflowY: 'auto',
            }}
          >
            <div style={{ width: '100%', maxWidth: 400 }}>

              <div style={{
                fontSize: 24, fontWeight: 800, color: '#fff',
                letterSpacing: '-.5px', marginBottom: 6,
              }}>
                Bem-vindo de volta
              </div>
              <div style={{ fontSize: 13, color: C.text2 }}>
                Faça login para acessar seu painel de operações.
              </div>

              {/* card */}
              <div style={{
                background: C.bg3, border: `1px solid ${C.bdr}`,
                borderRadius: 10, padding: '28px 28px 24px', marginTop: 24,
              }}>

                {senhaTrocada && !status && (
                  <div role="status" style={{
                    display: 'flex', alignItems: 'center', gap: 9,
                    background: C.greenBg, border: `1px solid ${C.greenB}`,
                    borderRadius: 7, padding: '10px 13px', marginBottom: 16,
                    fontSize: 12, color: C.text, lineHeight: 1.4,
                  }}>
                    A senha desta conta foi alterada. Entre com a senha nova.
                  </div>
                )}

                {/* Error bar */}
                {status && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 9,
                    background: C.redBg, border: `1px solid ${C.redB}`,
                    borderRadius: 7, padding: '10px 13px', marginBottom: 16,
                    animation: 'shakeIn .3s ease',
                  }}>
                    <span style={{ fontSize: 14, color: C.red, flexShrink: 0 }}>⚠</span>
                    <span style={{ fontSize: 12, color: C.text2, lineHeight: 1.4 }}>
                      <strong style={{ color: C.red }}>{status}</strong>
                    </span>
                  </div>
                )}

                <form onSubmit={handleLogin} autoComplete="on">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 20 }}>

                    {/* EMAIL */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                      <label htmlFor="login-email" style={{
                        fontSize: 10, fontWeight: 700, letterSpacing: '.1em',
                        textTransform: 'uppercase', color: C.text2,
                      }}>
                        E-mail profissional <span style={{ color: C.red, marginLeft: 2 }}>*</span>
                      </label>
                      <div className="field-input" style={{
                        background: C.bg4, border: `1px solid ${C.bdr2}`,
                        borderRadius: 7, padding: '11px 13px',
                        display: 'flex', alignItems: 'center', gap: 8, minHeight: 44,
                        transition: 'border-color .2s, background .2s',
                      }}>
                        <span style={{ fontSize: 14, color: C.text3, flexShrink: 0 }}>✉</span>
                        <input
                          id="login-email"
                          type="email"
                          name="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="seu@empresa.com.br"
                          autoComplete="username"
                          required
                          disabled={loading}
                        />
                      </div>
                    </div>

                    {/* PASSWORD */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                      <label htmlFor="login-password" style={{
                        fontSize: 10, fontWeight: 700, letterSpacing: '.1em',
                        textTransform: 'uppercase', color: C.text2,
                      }}>
                        Senha <span style={{ color: C.red, marginLeft: 2 }}>*</span>
                      </label>
                      <div className="field-input" style={{
                        background: C.bg4, border: `1px solid ${C.bdr2}`,
                        borderRadius: 7, padding: '11px 13px',
                        display: 'flex', alignItems: 'center', gap: 8, minHeight: 44,
                        transition: 'border-color .2s, background .2s',
                      }}>
                        <span style={{ fontSize: 14, color: C.text3, flexShrink: 0 }}>🔒</span>
                        <input
                          id="login-password"
                          type={showPassword ? 'text' : 'password'}
                          name="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="••••••••"
                          autoComplete="current-password"
                          required
                          disabled={loading}
                        />
                        <span
                          className="pw-eye"
                          onClick={() => setShowPassword((v) => !v)}
                          role="button"
                          tabIndex={0}
                          aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setShowPassword((v) => !v);
                            }
                          }}
                          style={{
                            fontSize: 14, color: C.text3, cursor: 'pointer',
                            flexShrink: 0, transition: 'color .15s', userSelect: 'none',
                          }}
                        >
                          {showPassword ? '🙈' : '👁'}
                        </span>
                      </div>
                    </div>

                  </div>

                  {/* Forgot password */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: -6 }}>
                    <span
                      className="forgot-link"
                      onClick={irParaRecuperar}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          irParaRecuperar();
                        }
                      }}
                      style={{
                        fontSize: 11, color: C.text3, cursor: 'pointer',
                        transition: 'color .15s',
                      }}
                    >
                      Esqueceu sua senha?
                    </span>
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    className="btn-submit"
                    disabled={isDisabled}
                    style={{
                      width: '100%', padding: 13, borderRadius: 7,
                      background: isDisabled ? C.text3 : C.green,
                      color: '#0A0F1A',
                      border: 'none', fontFamily: FONT, fontSize: 14, fontWeight: 700,
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      letterSpacing: '.02em',
                      boxShadow: isDisabled ? 'none' : '0 4px 16px rgba(0,214,124,.25)',
                      transition: 'all .18s',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      marginTop: 20, opacity: isDisabled ? 0.6 : 1,
                    }}
                  >
                    {loading
                      ? '⏳ Verificando...'
                      : retryIn > 0
                      ? `Aguarde ${retryIn}s...`
                      : 'Entrar no sistema'}
                  </button>
                </form>

                {/* Divider */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
                  <div style={{ flex: 1, height: 1, background: C.bdr }} />
                  <span style={{ fontSize: 11, color: C.text3 }}>ou</span>
                  <div style={{ flex: 1, height: 1, background: C.bdr }} />
                </div>

                {/* Register */}
                <div style={{ textAlign: 'center', fontSize: 12, color: C.text2 }}>
                  Ainda não tem conta?{' '}
                  <span
                    className="register-a"
                    onClick={() => navigate('/register')}
                    style={{
                      color: C.green, fontWeight: 600, cursor: 'pointer',
                      transition: 'color .15s',
                    }}
                  >
                    Cadastre-se grátis — 7 dias grátis →
                  </span>
                </div>

              </div>

              {/* Terms (decorativos por enquanto — sem href funcional). */}
              <div style={{
                fontSize: 10, color: C.text3, textAlign: 'center',
                marginTop: 16, lineHeight: 1.6,
              }}>
                Ao entrar, você concorda com nossos{' '}
                <span style={{ color: C.text3, textDecoration: 'underline', cursor: 'default' }}>
                  Termos de Uso
                </span>{' '}
                e{' '}
                <span style={{ color: C.text3, textDecoration: 'underline', cursor: 'default' }}>
                  Política de Privacidade
                </span>.
              </div>

              {/* Social proof strip */}
              <div style={{ marginTop: 32, textAlign: 'center' }}>
                <div style={{
                  fontSize: 10, color: C.text3, marginBottom: 10, letterSpacing: '.04em',
                }}>
                  Empresas que já blindam sua operação
                </div>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}>
                  {[
                    ['D', '#4F46E5'], ['C', '#0891B2'], ['N', '#047857'],
                    ['G', '#B45309'], ['S', '#7C3AED'],
                  ].map(([letter, bg], i) => (
                    <div key={letter} style={{
                      width: 24, height: 24, borderRadius: '50%',
                      border: `2px solid ${C.bg2}`,
                      marginLeft: i === 0 ? 0 : -8,
                      background: bg,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 9, fontWeight: 700, color: '#fff',
                    }}>
                      {letter}
                    </div>
                  ))}
                  <span style={{ fontSize: 11, color: C.text2, marginLeft: 6 }}>
                    <strong style={{ color: C.green }}>+12</strong> empresas ativas esta semana
                  </span>
                </div>
              </div>

            </div>
          </div>

        </div>
      </div>
    </>
  );
}
